import { BlockModel } from '../model/block/block-model';
import { type DraftModel, toDraftModelWork } from '../model/block/draft';
import {
  type InternalPrimitives,
  internalPrimitives,
} from '../model/block/zod';
import type { AssetsManager } from './assets';
import { fromJSON, toJSONWork } from './json';
import type { BlockSnapshot } from './type';

export type BlockSnapshotLeaf = Pick<
  BlockSnapshot,
  'id' | 'flavour' | 'props' | 'version'
>;

export type FromSnapshotPayload = {
  json: BlockSnapshotLeaf;
  assets: AssetsManager;
  children: BlockSnapshot[];
};

export type ToSnapshotPayload<Props extends object> = {
  model: DraftModel<BlockModel<Props>> | BlockModel<Props>;
  assets: AssetsManager;
};

export type SnapshotNode<Props extends object> = {
  id: string;
  flavour: string;
  version: number;
  props: Props;
};

export class BaseBlockTransformer<Props extends object = object> {
  protected _internal: InternalPrimitives = internalPrimitives;

  protected _propsFromSnapshot(propsJson: Record<string, unknown>) {
    return Object.fromEntries(
      Object.entries(propsJson).map(([key, value]) => {
        return [key, fromJSON(value)];
      })
    ) as Props;
  }

  protected *_propsToSnapshotWork(
    model: DraftModel | BlockModel
  ): Generator<void, Record<string, unknown>> {
    const draftModel =
      model instanceof BlockModel ? yield* toDraftModelWork(model) : model;
    const props: Record<string, unknown> = {};
    for (const key of draftModel.keys) {
      yield;
      const value = draftModel.props[key as keyof typeof draftModel.props];
      Object.defineProperty(props, key, {
        value: yield* toJSONWork(value),
        enumerable: true,
        configurable: true,
        writable: true,
      });
    }
    // Members and presentation index live in the page structure.
    const store = draftModel.store;
    const structure = store?.schema.flavourSchemaMap.get(draftModel.flavour)
      ?.model.structure;
    if (store && structure) {
      const { members, presentationIndex } = structure;
      if (members !== undefined) {
        props[members] = Object.fromEntries(
          store.pageStructure
            .members(draftModel.id)
            .map(member => [member, true])
        );
      }
      const pi = store.pageStructure.entry(draftModel.id)?.pi;
      if (presentationIndex !== undefined && pi !== undefined) {
        props[presentationIndex] = pi;
      }
    }
    return props;
  }

  protected _propsToSnapshot(model: DraftModel | BlockModel) {
    const work = this._propsToSnapshotWork(model);
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  }

  constructor(public readonly transformerConfigs: Map<string, unknown>) {}

  fromSnapshot({
    json,
  }: FromSnapshotPayload): Promise<SnapshotNode<Props>> | SnapshotNode<Props> {
    const { flavour, id, version, props: _props } = json;

    const props = this._propsFromSnapshot(_props);

    return {
      id,
      flavour,
      version: version ?? -1,
      props,
    };
  }

  *toSnapshotWork({
    model,
  }: ToSnapshotPayload<Props>): Generator<void, BlockSnapshotLeaf> {
    yield;
    const { id, flavour, version } = model;
    const props = yield* this._propsToSnapshotWork(model);
    return { id, flavour, version, props };
  }

  toSnapshot(payload: ToSnapshotPayload<Props>): BlockSnapshotLeaf {
    // Use the base representation here; subclass synchronous overrides retain
    // their own additional native fields and may implement toSnapshotWork.
    const work = BaseBlockTransformer.prototype.toSnapshotWork.call(
      this,
      payload
    );
    let next = work.next();
    while (!next.done) next = work.next();
    return next.value;
  }
}
